/**
 * TemplateEditor — Editor-Komponente fuer eine einzelne Mail-Vorlage.
 *
 * Felder: Subject (Input), BodyText (Textarea), BodyHtml (Textarea, optional).
 * Variablen-Chips: Klick fuegt Variable an Cursor-Position des zuletzt fokussierten Felds ein.
 * Vorschau: Rendert Template mit Beispiel-Daten via renderTemplatePreview (pure function).
 * Save: PUT /templates/:key via leadsApi.templates.update.
 *
 * CI-Farben: Save-Button #E9FF57/#1A1A1A, Vorschau-Button #1A1A1A/white.
 * NIE weisse Schrift auf Gelb.
 *
 * Phase 4 Plan 02 — TPL-02..05.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import { leadsApi } from '../../services/leadsApi';
import { renderTemplatePreview } from '../../lib/renderTemplatePreview';
import type { LeadTemplate } from '../../types';

const PREVIEW_VARS: Record<string, string> = {
  verein_name: 'FC Beispiel',
  verein_contact_person: 'Max Mustermann',
};

const AVAILABLE_VARS = ['{{verein_name}}', '{{verein_contact_person}}'];

type FieldName = 'subject' | 'bodyText' | 'bodyHtml';

interface TemplateEditorProps {
  template: LeadTemplate;
  onSaved: () => void;
}

function insertAtCursor(
  ref: RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
  value: string,
  setter: (v: string) => void,
  current: string,
) {
  const el = ref.current;
  if (!el) {
    setter(current + value);
    return;
  }
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const next = current.slice(0, start) + value + current.slice(end);
  setter(next);
  // Cursor nach eingefuegtem Text setzen
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + value.length, start + value.length);
  });
}

export default function TemplateEditor({ template, onSaved }: TemplateEditorProps) {
  const [subject, setSubject] = useState(template.subject);
  const [bodyText, setBodyText] = useState(template.bodyText);
  const [bodyHtml, setBodyHtml] = useState(template.bodyHtml ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<FieldName, string>>({
    subject: '',
    bodyText: '',
    bodyHtml: '',
  });
  const [preview, setPreview] = useState<{ subject: string; bodyText: string; bodyHtml: string | null } | null>(null);

  const [lastFocused, setLastFocused] = useState<FieldName>('bodyText');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyTextRef = useRef<HTMLTextAreaElement>(null);
  const bodyHtmlRef = useRef<HTMLTextAreaElement>(null);

  // Template wechselt (anderer Tab): State neu setzen
  useEffect(() => {
    setSubject(template.subject);
    setBodyText(template.bodyText);
    setBodyHtml(template.bodyHtml ?? '');
    setErrors({ subject: '', bodyText: '', bodyHtml: '' });
    setPreview(null);
    setSavedAt(null);
  }, [template.id, template.templateKey]);

  function handleChipClick(variable: string) {
    if (lastFocused === 'subject') {
      insertAtCursor(subjectRef as RefObject<HTMLInputElement | null>, variable, setSubject, subject);
    } else if (lastFocused === 'bodyHtml') {
      insertAtCursor(bodyHtmlRef as RefObject<HTMLTextAreaElement | null>, variable, setBodyHtml, bodyHtml);
    } else {
      insertAtCursor(bodyTextRef as RefObject<HTMLTextAreaElement | null>, variable, setBodyText, bodyText);
    }
  }

  function handlePreview() {
    const rendered = renderTemplatePreview(
      { subject, bodyText, bodyHtml: bodyHtml || null },
      PREVIEW_VARS,
    );
    setPreview(rendered);
  }

  async function handleSave() {
    const newErrors: Record<FieldName, string> = { subject: '', bodyText: '', bodyHtml: '' };
    let hasError = false;
    if (!subject.trim()) {
      newErrors.subject = 'Betreff ist Pflicht';
      hasError = true;
    }
    if (!bodyText.trim()) {
      newErrors.bodyText = 'Text-Inhalt ist Pflicht';
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) return;

    setSubmitting(true);
    try {
      await leadsApi.templates.update(template.templateKey, {
        subject,
        bodyText,
        bodyHtml: bodyHtml.trim() || null,
      });
      const now = new Date();
      setSavedAt(
        now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      );
      setErrors({ subject: '', bodyText: '', bodyHtml: '' });
      onSaved();
    } catch (err: any) {
      // Backend-Validation-Errors auswerten
      const details: Array<{ field: string; message: string }> =
        err?.response?.data?.error?.details ?? [];
      if (details.length > 0) {
        const fe: Record<FieldName, string> = { subject: '', bodyText: '', bodyHtml: '' };
        for (const d of details) {
          if (d.field === 'subject') fe.subject = d.message;
          else if (d.field === 'bodyText') fe.bodyText = d.message;
          else if (d.field === 'bodyHtml') fe.bodyHtml = d.message;
        }
        setErrors(fe);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputBase =
    'w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]';

  return (
    <div className="space-y-4">
      {/* Variablen-Chips */}
      <div>
        <p className="text-xs text-gray-500 mb-1">
          Variablen — Klick fuegt Variable an Cursor-Position des aktiven Felds ein:
        </p>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_VARS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => handleChipClick(v)}
              className="px-2 py-0.5 text-xs font-mono bg-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-300 transition-colors"
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Betreff</label>
        <input
          ref={subjectRef}
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onFocus={() => setLastFocused('subject')}
          className={inputBase}
          placeholder="Betreff der Mail"
        />
        {errors.subject && (
          <p className="mt-1 text-xs text-red-600">{errors.subject}</p>
        )}
      </div>

      {/* BodyText */}
      <div>
        <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
          Text-Inhalt (Pflicht)
        </label>
        <textarea
          ref={bodyTextRef}
          rows={8}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          onFocus={() => setLastFocused('bodyText')}
          className={inputBase + ' resize-y'}
          placeholder="Klartext-Version der Mail (wird von Mail-Clients ohne HTML-Unterstuetzung angezeigt)"
        />
        {errors.bodyText && (
          <p className="mt-1 text-xs text-red-600">{errors.bodyText}</p>
        )}
      </div>

      {/* BodyHtml */}
      <div>
        <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
          HTML-Inhalt (optional)
        </label>
        <textarea
          ref={bodyHtmlRef}
          rows={10}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          onFocus={() => setLastFocused('bodyHtml')}
          className={inputBase + ' resize-y font-mono text-xs'}
          placeholder="<p>HTML-Version der Mail (optional). Leer lassen fuer reine Text-Mail.</p>"
        />
        {errors.bodyHtml && (
          <p className="mt-1 text-xs text-red-600">{errors.bodyHtml}</p>
        )}
      </div>

      {/* Aktionen */}
      <div className="flex gap-3 items-center">
        <button
          type="button"
          onClick={handlePreview}
          className="px-4 py-2 text-sm font-medium rounded bg-[#1A1A1A] text-white hover:bg-gray-800 transition-colors"
        >
          Vorschau
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] transition-colors disabled:opacity-50"
        >
          {submitting ? 'Wird gespeichert...' : 'Speichern'}
        </button>
        {savedAt && (
          <span className="text-xs text-green-700">Gespeichert um {savedAt}</span>
        )}
      </div>

      {/* Vorschau-Panel */}
      {preview && (
        <div className="mt-4 border border-[#DAD9D9] rounded p-4 bg-gray-50">
          <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">
            Vorschau mit Beispiel-Daten
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            verein_name = "FC Beispiel", verein_contact_person = "Max Mustermann"
          </p>
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Betreff
              </span>
              <p className="mt-1 text-sm text-[#1A1A1A] font-medium">{preview.subject}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Text
              </span>
              <pre className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap font-sans">
                {preview.bodyText}
              </pre>
            </div>
            {preview.bodyHtml && (
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  HTML
                </span>
                <iframe
                  srcDoc={preview.bodyHtml}
                  title="HTML-Vorschau"
                  className="mt-1 w-full h-48 border border-[#DAD9D9] rounded bg-white"
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
